<template>
  <div>
    <Layout :containers="containers">
      <template v-slot:header>
        <HomeHeroBar
          :section="'home3'"
          @quickStartCardLinkClicked="quickStartCardLinkClicked"
          @gradientButtonClicked="signupClicked"
        />
      </template>
      <template v-slot:title-row>
        <div class="section-padding">
          <TitleRow
            :html="true"
            :title="`<span style='font-weight:300;'>Why use our online will</span> <span style='font-weight:500;'>instead of a template?</span>`"
            :description="`Many people search for a will template. But instead of leaving you to figure it out on your own, our free online service does it all – safely, simply, and with expert support.`"
          />
        </div>
      </template>
      <template v-slot:thousands>
        <div class="section-padding">
          <ImageTextBanner
            :imageUrl="whitelabel.images.home3thousandsImage"
            :title="'Here’s why thousands choose our online will service instead:'"
            :paragraphOne="`<ul class='list-disc'>
  <li>It’s 100% free to create, validate and store your will</li>
  <li>Step-by-step process - no legal jargon</li>
  <li>Legally valid in South Africa</li>
  <li>Update anytime with ease</li>
  <li>Backed by Old Mutual’s expertise</li>
</ul>`"
            :reversed="false"
            :bottomMargin="true"
            :buttonText="'CREATE YOUR FREE WILL'"
            @gradientButtonClicked="createYourWill"
          />
        </div>
      </template>
      <template v-slot:info-tiles>
        <div class="section-padding">
          <InfoTilesContainer
            :tiles="infoTiles"
            :alternativeTitle="`<span
            style='
              font-family: Montserrat;
              font-size: 28px;
              font-weight: 500;
              line-height: 34px;
              letter-spacing: -0.0025em;
              text-align: center;'
            >How does it work?</span
          >`"
            :buttonText="'CREATE YOUR FREE WILL'"
            @gradientButtonClicked="createYourWill"
          />
        </div>
      </template>
      <template v-slot:create-your-will>
        <CreateYourWillBanner
          :alternativeBanner="true"
          :bannerText="`<strong style='font-weight:500;'>Join 23,000+ South Africans</strong> who’ve created their wills online with Old Mutual since 2023`"
        />
      </template>
      <template v-slot:downloadable-template>
        <div class="section-padding">
          <ImageTextBanner
            :imageUrl="whitelabel.images.home3DownloadbleTemplateImage"
            :title="'Why we don’t offer a downloadable template'"
            :paragraphOne="`<strong>Templates leave too much to chance. That’s why we offer a better way – fully online, fully supported.</strong>
            <ul class='mt-5 mb-5'>
            <li>No formatting errors</li>
            <li>No legal guesswork</li>
            <li>No risk of invalid clauses</li>
            </ul>
            <p>Our process ensures your will is valid, clear, and tailored to South African law – without the stress of DIY templates</p>`"
            :reversed="true"
            :bottomMargin="true"
            :buttonText="'CREATE YOUR FREE WILL'"
            @gradientButtonClicked="createYourWill"
          />
        </div>
      </template>
      <template v-slot:faqs>
        <div class="section-padding"></div>
        <TitleRow :html="true" :title="`Frequently Asked Questions`" :description="''" />
        <Accordion :accordionData="accordionData" :html="true" />
      </template>
      <template v-slot:forget-templates>
        <CreateYourWillBanner
          :alternativeBanner="true"
          :bannerText="`Forget templates. <strong>Create your will online – free, fast, and legally sound.</strong>`"
          :buttonText="'CREATE YOUR FREE WILL'"
          @createYourWill="createYourWill"
          :pb0="true"
        />
      </template>
      <template v-slot:callusback>
        <EstateAdministrationCostCalculatorFooter
          @requestACall="requestACall"
          v-if="whitelabel.brand !== 'quickwill'"
        />
      </template>
    </Layout>
    <SuccessSnackbar v-model="showAlert" :message="alertMessage" />
    <RequestACallDialog
      :showRequestACallDialog.sync="showRequestACallDialog"
      @success="showSuccessMessage"
    />
  </div>
</template>

<script>
import HomeHeroBar from "./HomeHeroBar.vue";
import ButtonTilesContainer from "../ButtonTilesContainer.vue";
import InfoTilesContainer from "../InfoTilesContainer.vue";
import CreateYourWillBanner from "../CreateYourWillBanner.vue";
import ImageTextBanner from "../ImageTextBanner.vue";
import TestimonialsSection from "../TestimonialsSection.vue";
import HR from "../../common/HR.vue";
import Layout from "../../common/layouts/Layout.vue";
import DynamicNavDrawer from "../../guest/DynamicNavDrawer.vue";
import DrawerSignUp from "../../guest/DrawerSignUp.vue";
import DrawerSignIn from "../../guest/DrawerSignIn.vue";
import SuccessSnackbar from "../../common/SuccessSnackbar.vue";
import EstateAdministrationCostCalculatorFooter from "../EstateCostCalculator/EstateAdministrationCostCalculatorFooter.vue";
import RequestACallDialog from "../dialogs/RequestACallDialog.vue";
import Accordion from "../../common/Accordion.vue";
import TitleRow from "../../common/TitleRow.vue";

export default {
  name: "Home3",
  metaInfo() {
    return {
      link: [
        {
          rel: "canonical",
          href: this.whitelabel.canonicalUrls.homepage,
        },
      ],
    };
  },
  props: {
    openSignupDrawerImmediately: {
      required: false,
      default: false,
      type: Boolean,
    },
    isReferralLink: {
      required: false,
      default: false,
      type: Boolean,
    },
    isActivationLink: {
      required: false,
      default: false,
      type: Boolean,
    },
    isPasswordResetLink: {
      required: false,
      default: false,
      type: Boolean,
    },
    verifyUserLinkCode: {
      required: false,
      default: null,
      type: Object,
    },
  },
  components: {
    HomeHeroBar,
    ButtonTilesContainer,
    InfoTilesContainer,
    CreateYourWillBanner,
    ImageTextBanner,
    TestimonialsSection,
    DynamicNavDrawer,
    HR,
    Layout,
    DrawerSignUp,
    DrawerSignIn,
    SuccessSnackbar,
    EstateAdministrationCostCalculatorFooter,
    RequestACallDialog,
    Accordion,
    TitleRow,
  },
  data(props) {
    return {
      signUpDrawerOpen: props.openSignupDrawerImmediately,
      signInDrawerOpen: false,
      showAlert: false,
      alertMessage: null,
      countryPattern: require("@/assets/shared/redesign/backgrounds/country-pattern.svg"),
      greenAccentOne: require("@/assets/shared/redesign/backgrounds/green-accent-1.svg"),
      greenAccentTwo: require("@/assets/shared/redesign/backgrounds/green-accent-2.svg"),
      purpleAccent: require("@/assets/shared/redesign/backgrounds/purple-accent.svg"),
      tribalPattern: require(`@/assets/old_mutual/homepage/generic.svg`),
      showRequestACallDialog: false,
    };
  },
  mounted() {
    if (localStorage.getItem("isLandingSimple")) {
      localStorage.removeItem("isLandingSimple");
      location.reload();
    }
  },
  methods: {
    successResetPassword() {
      this.signInDrawerOpen = false;
      this.alertMessage =
        this.whitelabel.authentication.resetPassword.successMessage;
      this.showAlert = true;
    },
    loginClicked() {
      this.signUpDrawerOpen = false;
      this.signInDrawerOpen = true;
    },
    signupClicked() {
      this.$router.push("/register");
    },
    drawerClosed() {
      this.signUpDrawerOpen = false;
    },
    signInDrawerClosed() {
      this.signInDrawerOpen = false;
    },
    quickStartCardLinkClicked(routeName) {
      if (routeName === "SignUpPage") {
        this.signUpDrawerOpen = true;
      } else {
        this.$router.push({ name: routeName });
      }
    },
    createYourWill() {
      this.$router.push("/register");
    },
    estateCostCalculator() {
      this.$router.push({
        name: "EstateAdministrationCostCalculator",
      });
    },
    requestACall() {
      this.showRequestACallDialog = true;
    },
    showSuccessMessage(success) {
      this.$emit("success", success);
    },
  },
  computed: {
    containers() {
      return [
        {
          name: "header",
          background: "countryPattern",
          class: "hero-banner",
        },
        {
          name: "title-row",
          fadedRow: true,
          background: "countryPattern",
        },
        {
          name: "thousands",
          hr: true,
        },
        {
          name: "info-tiles",
        },
        {
          name: "create-your-will",
        },
        {
          name: "downloadable-template",
        },
        {
          name: "faqs",
        },
        {
          name: "forget-templates",
        },
        { name: "callusback" },
      ];
    },
    buttonTiles() {
      return [
        {
          imageUrl: this.whitelabel.images.home1ButtonTileOne,
          title: "",
          description: `Answer a few guided questions`,
        },
        {
          imageUrl: this.whitelabel.images.home1ButtonTileTwo,
          title: "",
          description: `Review your personalised will`,
        },
        {
          imageUrl: this.whitelabel.images.home1ButtonTileThree,
          title: "",
          description: `Print, sign and validate`,
        },
      ];
    },
    infoTiles() {
      return [
        {
          imageUrl: this.whitelabel.images.home3InfoTitleOne,
          title: "Answer a few guided questions",
          description: "",
        },
        {
          imageUrl: this.whitelabel.images.home3InfoTitleTwo,
          title: "Review your personalised will",
          description: "",
        },
        {
          imageUrl: this.whitelabel.images.home3InfoTitleThree,
          title: "Print, sign and validate",
          description: "",
        },
      ];
    },
    accordionData() {
      return this.whitelabel.FAQs3;
    },
  },
};
</script>
<style lang="scss" scoped>
@media only screen and (min-width: 601px) and (max-width: 807px) {
  .mobile-padding-top {
    padding-top: 111px;
  }
}
@media only screen and (min-width: 0px) and (max-width: 600px) {
  .mobile-padding-top {
    padding-top: 133px;
  }
}
</style>
